import React, { useEffect, useRef, useState } from "react";
import { Receipt } from "../services/receipt-service";
import {
  ReceiptCategory,
  ReceiptCategoryService,
} from "../../receipt-categories/services/receipt-category-service";
import { isSuccess } from "../../../models/response/base-response";

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (data: Partial<Receipt>) => void;
  item?: Receipt | null;
}

function toInputDateTime(value?: string | null) {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) return value.slice(0, 16);
  const isoSpace = value.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/
  );
  if (isoSpace)
    return `${isoSpace[1]}-${isoSpace[2]}-${isoSpace[3]}T${isoSpace[4]}:${isoSpace[5]}`;
  const vn = value.match(
    /^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})/
  );
  if (vn)
    return `${vn[3]}-${vn[2]}-${vn[1]}T${vn[4]}:${vn[5]}`;
  const d = new Date(value);
  if (!isNaN(d.getTime())) {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(
      d.getMonth() + 1
    )}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
      d.getMinutes()
    )}`;
  }
  return "";
}

export const ReceiptCreate: React.FC<Props> = ({
  open,
  onClose,
  onSave,
  item,
}) => {
  const [form, setForm] = useState<
    Omit<Partial<Receipt>, "amount"> & { amount: string }
  >(
    item
      ? { ...item, amount: String(item.amount ?? 0) }
      : { type: 1, amount: "0", note: "" }
  );
  const [categories, setCategories] = useState<ReceiptCategory[]>([]);
  const { fetch: listCategories } = ReceiptCategoryService.list();
  const listCategoriesRef = useRef(listCategories);
  useEffect(() => {
    listCategoriesRef.current = listCategories;
  }, [listCategories]);

  useEffect(() => {
    if (item) {
      setForm({
        ...item,
        transaction_date: toInputDateTime(item.transaction_date),
        amount: String(item.amount ?? 0),
      });
    } else {
      setForm({ type: 1, amount: "0", note: "" });
    }
  }, [item]);

  useEffect(() => {
    const load = async () => {
      if (!open) return;
      const res = await listCategoriesRef.current({
        status: 1,
        limit: 1000,
      } as any);
      if (res && isSuccess(res)) {
        const d: any = res.data || {};
        const list: ReceiptCategory[] = Array.isArray(d.list)
          ? d.list
          : [];
        setCategories(list);
      } else setCategories([]);
    };
    void load();
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50 animate-fadeIn">
      <div className="bg-white rounded-2xl w-[600px] shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-green-500 text-white px-6 py-3 flex justify-between items-center">
          <h3 className="text-lg font-semibold">
            {item ? "Cập nhật Phiếu Thu/Chi" : "Tạo Phiếu Thu/Chi"}
          </h3>
          <button
            onClick={onClose}
            className="text-white text-2xl leading-none hover:opacity-80"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4 text-gray-800">
          <div className="grid grid-cols-2 gap-4">
            {/* Loại phiếu */}
            <div>
              <label className="text-sm font-medium text-gray-600 mb-1 block">
                Loại phiếu
              </label>
              <select
                className="border border-gray-300 rounded-lg w-full px-3 py-2 focus:ring-2 focus:ring-green-400 focus:border-green-400 outline-none"
                value={form.type ?? 1}
                onChange={(e) =>
                  setForm({ ...form, type: Number(e.target.value) })
                }
              >
                <option value={1}>Phiếu Thu</option>
                <option value={2}>Phiếu Chi</option>
              </select>
            </div>

            {/* Danh mục */}
            <div>
              <label className="text-sm font-medium text-gray-600 mb-1 block">
                Danh mục
              </label>
              <select
                className="border border-gray-300 rounded-lg w-full px-3 py-2 focus:ring-2 focus:ring-green-400 focus:border-green-400 outline-none"
                value={form.receipt_category_id ?? 0}
                onChange={(e) =>
                  setForm({
                    ...form,
                    receipt_category_id: Number(e.target.value),
                  })
                }
              >
                <option value={0}>-- Chọn danh mục --</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.code} - {c.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Số chứng từ */}
            <div>
              <label className="text-sm font-medium text-gray-600 mb-1 block">
                Số chứng từ
              </label>
              <input
                className="border border-gray-300 rounded-lg w-full px-3 py-2 focus:ring-2 focus:ring-green-400 focus:border-green-400 outline-none"
                placeholder="Nhập số chứng từ"
                value={form.document_number ?? ""}
                onChange={(e) =>
                  setForm({ ...form, document_number: e.target.value })
                }
              />
            </div>

            {/* Ngày giao dịch */}
            <div>
              <label className="text-sm font-medium text-gray-600 mb-1 block">
                Ngày hạch toán
              </label>
              <input
                type="datetime-local"
                className="border border-gray-300 rounded-lg w-full px-3 py-2 focus:ring-2 focus:ring-green-400 focus:border-green-400 outline-none"
                value={toInputDateTime(form.transaction_date)}
                onChange={(e) =>
                  setForm({ ...form, transaction_date: e.target.value })
                }
              />
            </div>

            {/* Số tiền */}
            <div className="col-span-2">
              <label className="text-sm font-medium text-gray-600 mb-1 block">
                Số tiền
              </label>
              <input
                type="text"
                inputMode="decimal"
                className="border border-gray-300 rounded-lg w-full px-3 py-2 focus:ring-2 focus:ring-green-400 focus:border-green-400 outline-none text-right font-semibold text-gray-900"
                value={Number(form.amount).toLocaleString("en-US")}
                onChange={(e) => {
                  const rawValue = e.target.value.replace(/[^0-9]/g, "");
                  if (rawValue.length > 20) return;
                  setForm({ ...form, amount: rawValue || "0" });
                }}
              />
            </div>

            {/* Ghi chú */}
            <div className="col-span-2">
              <label className="text-sm font-medium text-gray-600 mb-1 block">
                Ghi chú
              </label>
              <textarea
                className="border border-gray-300 rounded-lg w-full px-3 py-2 h-24 focus:ring-2 focus:ring-green-400 focus:border-green-400 outline-none resize-none"
                placeholder="Nhập nội dung ghi chú"
                value={form.note ?? ""}
                onChange={(e) =>
                  setForm({ ...form, note: e.target.value })
                }
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition font-medium"
          >
            Hủy
          </button>
          <button
            onClick={() => onSave({ ...form, amount: Number(form.amount) })}
            className="px-5 py-2 rounded-lg bg-green-600 text-white font-medium hover:bg-green-700 transition"
          >
            Lưu
          </button>
        </div>
      </div>
    </div>
  );
};